import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';

import { fetchWordPressSyncWarningGroups, runWordPressBulkAction } from './service';
import {
  WORDPRESS_SYNC_WARNING_AUTO_CLEAR_TEXT,
  WORDPRESS_SYNC_WARNING_TARGET_TYPE_OPTIONS,
  getSafeWordPressSyncWarningUrl,
  getWordPressPublicationTargetLabel,
  getWordPressSyncWarningInstruction,
  getWordPressSyncWarningTargetTypeLabel,
} from './syncWarningsUi';
import type {
  WordPressPublicationType,
  WordPressSyncWarningGroup,
  WordPressSyncWarningTargetType,
} from './types';

const TARGET_OPTIONS: WordPressPublicationType[] = ['dvh', 'foodservice', 'wine', 'recipe'];

type MissingInWordPressViewProps = {
  reloadKey?: number;
  onMessage?: (message: { severity: 'success' | 'error' | 'info'; text: string } | null) => void;
  onChangeComplete?: () => void;
  onOpenAsset: (target: WordPressPublicationType, assetId: string) => void;
};

export default function MissingInWordPressView({ reloadKey = 0, onMessage, onChangeComplete, onOpenAsset }: MissingInWordPressViewProps) {
  const [groups, setGroups] = useState<WordPressSyncWarningGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [targetFilter, setTargetFilter] = useState<WordPressPublicationType | ''>('');
  const [typeFilter, setTypeFilter] = useState<WordPressSyncWarningTargetType | 'none' | ''>('');
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchWordPressSyncWarningGroups();
      setGroups(result.groups);
    } catch (loadError: any) {
      setError(loadError?.response?.data?.message || loadError?.message || 'Kunde inte hämta synkvarningarna.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGroups();
  }, [loadGroups, reloadKey]);

  const filteredGroups = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('sv-SE');
    return groups.filter(group => {
      if (targetFilter && group.target !== targetFilter) return false;
      if (typeFilter === 'none' && group.targetType) return false;
      if (typeFilter && typeFilter !== 'none' && group.targetType !== typeFilter) return false;
      if (!needle) return true;
      return [group.value, group.wordpressTargetLabel, group.wordpressTarget, ...group.fieldLabels]
        .some(text => (text || '').toLocaleLowerCase('sv-SE').includes(needle));
    });
  }, [groups, search, targetFilter, typeFilter]);

  const selectedGroup = useMemo(
    () => filteredGroups.find(group => group.key === selectedKey) || null,
    [filteredGroups, selectedKey],
  );

  const handleResync = useCallback(async (group: WordPressSyncWarningGroup) => {
    const assetIds = group.assets.map(asset => asset.sourceAssetId);
    if (assetIds.length === 0) return;
    try {
      setSyncing(true);
      const result = await runWordPressBulkAction({ assetIds, type: group.target, action: 'request-sync' });
      const firstFailure = result.failures[0];
      onMessage?.({
        severity: result.failedCount > 0 ? 'error' : 'success',
        text: result.failedCount > 0
          ? `${result.successCount} skickades för ny synk, ${result.failedCount} misslyckades.${firstFailure ? ` Första fel: ${firstFailure.assetId} - ${firstFailure.message}` : ''}`
          : `${result.successCount} av ${result.totalAssets} poster skickades för ny synk till WordPress. ${WORDPRESS_SYNC_WARNING_AUTO_CLEAR_TEXT}`,
      });
      await loadGroups();
      onChangeComplete?.();
    } catch (syncError: any) {
      onMessage?.({ severity: 'error', text: syncError?.response?.data?.message || syncError?.message || 'Kunde inte begära ny synk.' });
    } finally {
      setSyncing(false);
    }
  }, [loadGroups, onChangeComplete, onMessage]);

  const selectedActionUrl = selectedGroup ? getSafeWordPressSyncWarningUrl(selectedGroup.actionUrl) : null;

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Sök värde, WordPress-mål eller fält"
          size="small"
          sx={{ flex: '1 1 280px', minWidth: { xs: '100%', sm: 280 } }}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8, opacity: 0.65 }} />,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            displayEmpty
            value={targetFilter}
            onChange={event => setTargetFilter(String(event.target.value || '') as WordPressPublicationType | '')}
          >
            <MenuItem value="">Alla mål</MenuItem>
            {TARGET_OPTIONS.map(target => (
              <MenuItem key={target} value={target}>{getWordPressPublicationTargetLabel(target)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select
            displayEmpty
            value={typeFilter}
            onChange={event => setTypeFilter(String(event.target.value || '') as WordPressSyncWarningTargetType | 'none' | '')}
          >
            <MenuItem value="">Alla typer</MenuItem>
            {WORDPRESS_SYNC_WARNING_TARGET_TYPE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
            <MenuItem value="none">Övrigt</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => { void loadGroups(); }} disabled={loading}>
          Uppdatera
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        Värden som WordPress rapporterade som saknade vid senaste synken, för publicerade poster. {WORDPRESS_SYNC_WARNING_AUTO_CLEAR_TEXT}
      </Typography>

      {error && <Alert severity="error" sx={{ py: 0.25 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', xl: 'row' }, flex: 1, minHeight: 0 }}>
        <Paper variant="outlined" sx={{ flex: { xs: '1 1 auto', xl: '1 1 60%' }, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {loading && groups.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : filteredGroups.length === 0 ? (
            <Box sx={{ p: 1.5 }}>
              <Alert severity="info">
                {groups.length === 0 ? 'Inga synkvarningar från WordPress just nu.' : 'Inga synkvarningar matchade filtret.'}
              </Alert>
            </Box>
          ) : (
            <TableContainer sx={{ flex: 1, minHeight: 0 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Mål</TableCell>
                    <TableCell>Typ</TableCell>
                    <TableCell>I WordPress</TableCell>
                    <TableCell>Värde</TableCell>
                    <TableCell>Athena-fält</TableCell>
                    <TableCell align="right">Antal</TableCell>
                    <TableCell>Åtgärd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGroups.map(group => {
                    const actionUrl = getSafeWordPressSyncWarningUrl(group.actionUrl);
                    return (
                      <TableRow
                        key={group.key}
                        hover
                        selected={group.key === selectedKey}
                        onClick={() => setSelectedKey(group.key)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{getWordPressPublicationTargetLabel(group.target)}</TableCell>
                        <TableCell>{getWordPressSyncWarningTargetTypeLabel(group.targetType)}</TableCell>
                        <TableCell>{group.wordpressTargetLabel || group.wordpressTarget || '–'}</TableCell>
                        <TableCell>
                          {group.value
                            ? <Chip size="small" color="warning" label={group.value} sx={{ maxWidth: 240 }} />
                            : <Typography variant="body2" color="text.secondary">–</Typography>}
                        </TableCell>
                        <TableCell>{group.fieldLabels.join(', ') || '–'}</TableCell>
                        <TableCell align="right">{group.assetCount}</TableCell>
                        <TableCell onClick={event => event.stopPropagation()}>
                          {actionUrl && (
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              href={actionUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Öppna i WordPress
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ flex: { xs: '1 1 auto', xl: '1 1 40%' }, p: 2, overflowY: 'auto', minHeight: 0, minWidth: 0 }}>
          {!selectedGroup ? (
            <Typography variant="body2" color="text.secondary">
              Välj en rad för att se vilka assets som berörs.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="subtitle1">
                  {selectedGroup.value || selectedGroup.wordpressTargetLabel || selectedGroup.wordpressTarget || 'Synkvarning'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getWordPressPublicationTargetLabel(selectedGroup.target)} · {getWordPressSyncWarningTargetTypeLabel(selectedGroup.targetType)}
                  {(selectedGroup.wordpressTargetLabel || selectedGroup.wordpressTarget) ? ` · ${selectedGroup.wordpressTargetLabel || selectedGroup.wordpressTarget}` : ''}
                </Typography>
              </Box>
              <Typography variant="body2">
                {getWordPressSyncWarningInstruction({
                  code: selectedGroup.code,
                  fieldLabel: selectedGroup.fieldLabels.join(', '),
                  values: selectedGroup.value ? [selectedGroup.value] : [],
                  resolvedTargetType: selectedGroup.targetType,
                  wordpressTargetLabel: selectedGroup.wordpressTargetLabel,
                  wordpressTarget: selectedGroup.wordpressTarget,
                  message: selectedGroup.message,
                })}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {selectedActionUrl && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    href={selectedActionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Öppna i WordPress
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SyncIcon />}
                  onClick={() => { void handleResync(selectedGroup); }}
                  disabled={syncing || selectedGroup.assets.length === 0}
                >
                  {syncing ? 'Skickar...' : `Synka berörda igen (${selectedGroup.assets.length})`}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Använd "Synka berörda igen" när värdet har skapats i WordPress. Posterna skickas till WordPress igen utan att ändras i Athena.
              </Typography>
              <Stack spacing={0.75}>
                {selectedGroup.assets.map(asset => (
                  <Box
                    key={asset.sourceAssetId}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {asset.title || asset.sourceAssetId}
                      </Typography>
                      {asset.fieldLabels.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {asset.fieldLabels.join(', ')}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => onOpenAsset(selectedGroup.target, asset.sourceAssetId)}
                      sx={{ flexShrink: 0 }}
                    >
                      Öppna i fliken
                    </Button>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
